/**
 * The ONE place the Item Mapping screens turn their rows into the PUT/validate payload.
 * itemmap.js and QRitemmap.js used to each carry their own copy (with different unit
 * handling — QR sent raw kitchen units); now both call this.
 *
 * Rules:
 *  - per-plate quantity = displayed quantity / plate count, at 4 dp
 *  - kg/L are sent as g/ml (toBaseUnitOrKeep); kitchen units pass through for the
 *    server's Layer A.5 to resolve or refuse — never invent a factor here
 *  - same-name rows in the same family are summed in the base unit; across families
 *    they are kept apart so the server refuses them with a clear message instead of
 *    this code guessing a density
 */
import { toBaseUnitOrKeep, getUnitFamily, toCanonicalUnit } from './unitDisplay';

const PLACEHOLDER_NAME = 'new ingredient';

const round4 = (value) => Number(Number(value).toFixed(4));

export function buildPerPlatePayload(rows, plateCount, { getDisplayQty }) {
  const divisor = Number(plateCount) >= 1 ? Number(plateCount) : 1;
  const roundedToZero = [];
  const perPlate = [];

  for (const row of rows || []) {
    const name = String(row?.name || '').trim();
    if (!name || name.toLowerCase() === PLACEHOLDER_NAME) continue;

    const displayQty = Number(getDisplayQty(row)) || 0;
    const quantity = round4(displayQty / divisor);
    if (displayQty > 0 && quantity <= 0.0001) {
      roundedToZero.push(name);
    }
    if (!(quantity > 0.0001)) continue;

    const base = toBaseUnitOrKeep(quantity, row.unit || 'g');
    perPlate.push({ name, unit: base.unit, quantity: round4(base.value) });
  }

  // Merge same-name rows only when they share a family; the family is already in base
  // units after toBaseUnitOrKeep, so a plain sum is correct.
  const merged = [];
  for (const ing of perPlate) {
    const family = getUnitFamily(toCanonicalUnit(ing.unit));
    const target = merged.find(
      (m) => m.name.toLowerCase() === ing.name.toLowerCase()
        && family
        && getUnitFamily(toCanonicalUnit(m.unit)) === family
    );
    if (target) {
      target.quantity = round4(target.quantity + ing.quantity);
    } else {
      merged.push({ ...ing });
    }
  }

  return { ingredients: merged, roundedToZero };
}
